import { useState, useCallback } from 'react';
import { Modal } from '@swtorbit/ui/react';
import { useSocketContext } from '@swtorbit/shared-react-hooks';
import {
  SocketEvent,
  type GamePlayerInfo,
  type FprRegistrationResponses,
  type FprRegistrationRole,
} from '@swtorbit/shared-socket-events';

interface Props {
  player: GamePlayerInfo;
  roomCode: string;
  onClose: () => void;
}

const ROLES: { value: FprRegistrationRole; label: string }[] = [
  { value: 'tank', label: 'Tank' },
  { value: 'healer', label: 'Healer' },
  { value: 'dps', label: 'DPS' },
];

export function PlayerRegistrationModal({ player, roomCode, onClose }: Props) {
  const { socket } = useSocketContext();

  const existing = player.fprRegistration;
  const [roles, setRoles] = useState<FprRegistrationRole[]>(existing?.roles ?? []);
  const [availableAsLeader, setAvailableAsLeader] = useState(existing?.availableAsLeader ?? false);
  const [knowsMmMechanics, setKnowsMmMechanics] = useState(existing?.knowsMmMechanics ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleRole = useCallback((role: FprRegistrationRole) => {
    setRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  }, []);

  const handleSave = useCallback(() => {
    if (!socket) return;
    if (roles.length === 0) {
      setError('Select at least one role');
      return;
    }
    setSaving(true);
    setError(null);

    const responses: FprRegistrationResponses = { roles, availableAsLeader, knowsMmMechanics };

    socket.emit(SocketEvent.FPR_SET_PLAYER_REGISTRATION, {
      roomCode,
      playerId: player.playerId,
      responses,
    });

    socket.once(SocketEvent.FPR_PLAYER_REGISTRATION_SET, (res: { success: boolean; error?: string }) => {
      setSaving(false);
      if (res.success) {
        onClose();
      } else {
        setError(res.error ?? 'Failed to save registration');
      }
    });
  }, [socket, roomCode, player.playerId, roles, availableAsLeader, knowsMmMechanics, onClose]);

  const displayName = player.characterName || player.discordUsername || player.playerId.slice(-8);

  return (
    <Modal open onClose={onClose} title={<>Registration — {displayName}</>} footer={
<>
        <button className="btn btn--outline btn--slate" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </>
}>
        {error && <p className="text-red mb-xs">{error}</p>}

        <div className="field">
          <label>Roles (select all that apply)</label>
          <div className="d-flex gap-xs mt-2xs">
            {ROLES.map(({ value, label }) => (
              <label key={value} className="d-flex align-items-center gap-2xs" style={{ cursor: 'pointer' }}>
                <input
                  className="checkbox"
                  type="checkbox"
                  checked={roles.includes(value)}
                  onChange={() => toggleRole(value)}
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        <div className="field mt-sm">
          <label className="d-flex align-items-center gap-2xs" style={{ cursor: 'pointer' }}>
            <input
              className="checkbox"
              type="checkbox"
              checked={availableAsLeader}
              onChange={e => setAvailableAsLeader(e.target.checked)}
            />
            Available as Team Leader
          </label>
        </div>

        <div className="field mt-xs">
          <label className="d-flex align-items-center gap-2xs" style={{ cursor: 'pointer' }}>
            <input
              className="checkbox"
              type="checkbox"
              checked={knowsMmMechanics}
              onChange={e => setKnowsMmMechanics(e.target.checked)}
            />
            Knows MM Mechanics
          </label>
        </div>

        {player.fprRegistrationManual && (
          <p className="text-slate fs-sm mt-sm">Previously entered manually by admin.</p>
        )}
      </Modal>
  );
}
